import { useState } from "react";
import { Link, useNavigate, useLocation, useParams } from "react-router-dom";
import { api, setToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button, Input } from "@/components/ui/Primitives";
import { BRAND_LOGO_URL } from "@/components/Brand";
import { clearPrivateClientState } from "@/lib/private-client-state";
import { MathCaptcha, useCaptcha } from "@/components/MathCaptcha";
import { usePageTitle } from "@/lib/seo";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Lock, ShieldCheck, Mail, Building2, Home, Eye, AlertCircle } from "lucide-react";
import { ClosedEyeLashes } from "@/components/icons/ClosedEyeLashes";

const EMAIL = "contact@centralrentals.ca";

const PORTALS = {
  landlord: {
    title: "Landlord sign-in",
    subtitle: "Manage your properties, tenants, and rent.",
    icon: Building2,
    accentClass: "text-emerald-700",
    badgeText: "Landlord portal",
    altPortal: "tenant",
    altLabel: "Sign in as tenant",
    signupHint: "New here?",
    signupTo: "/signup?role=landlord",
  },
  tenant: {
    title: "Tenant sign-in",
    subtitle: "Pay rent, ask for repairs, and message your landlord.",
    icon: Home,
    accentClass: "text-sky-700",
    badgeText: "Tenant portal",
    altPortal: "landlord",
    altLabel: "Sign in as landlord",
    signupHint: "New here?",
    signupTo: "/signup?role=tenant",
  },
};

export default function Login() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const params = useParams();
  const portalKey = params.portal === "tenant" ? "tenant" : params.portal === "landlord" ? "landlord" : null;

  const [portal, setPortal] = useState(portalKey || "landlord");
  // Support `?email=you@example.ca` in the URL so the operator can bookmark
  // a one-click login (browser password manager fills the rest). NEVER put
  // a password in the URL — that leaks in browser history, chat, and
  // referrer headers. Email-only is safe.
  const [email, setEmail] = useState(() => {
    if (typeof window === "undefined") return "";
    const q = new URLSearchParams(window.location.search).get("email") || "";
    return q.trim();
  });
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const captcha = useCaptcha();

  const cfg = PORTALS[portal];
  usePageTitle(`${cfg.title} — Central Rentals Canada`);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(""); setEmailError(""); setPasswordError("");
    if (!captcha.token || !captcha.answer.trim()) { setError("Complete the security check before signing in."); return; }
    setSubmitting(true);
    try {
      // Pre-flight: call /auth/login directly so we can verify role BEFORE
      // committing the token + user state (PostAuthGate would otherwise
      // redirect to /dashboard the instant user state is set).
      const { data } = await api.post(
        "/auth/login",
        { email: email.trim(), password },
        { headers: captcha.headers() },
      );
      const u = data.user;
      // Admins and investors can sign in from either portal (admin has full access;
      // investor is a cross-cutting role). Only enforce the portal check for
      // strict landlord/tenant mismatches.
      const roleOk = !u?.role
        || u.role === portal
        || u.role === "investor"
        || u.role === "admin";
      if (!roleOk) {
        setError(`This email is registered as a ${u.role}. Switch to the ${u.role} portal to continue.`);
        setSubmitting(false);
        return;
      }
      // Commit auth state.
      await clearPrivateClientState();
      setToken(data.session_token);
      setUser(u);
      try { localStorage.setItem("crc_last_portal", u?.role === "tenant" ? "tenant" : "landlord"); } catch { /* noop */ }
      toast.success("Welcome back");
      if (!u?.role) nav("/role-select", { replace: true });
      else {
        // Respect ?next=… from the URL so flows like /team/accept land
        // back on the right page after sign-in.
        const sp = new URLSearchParams(loc.search);
        const next = sp.get("next");
        nav(next || loc.state?.from || "/dashboard", { replace: true });
      }
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      // Backend returns 401 for both unknown email + wrong password; show a single clear inline error.
      if (status === 400 && /security\s+check|captcha/i.test(detail || "")) {
        setError(detail || "Security check failed. Please answer the question again.");
        captcha.refresh();
      } else if (status === 404 || /not\s*found|no such user|unknown/i.test(detail || "")) {
        setEmailError("We don't recognise this email. Double-check spelling or create a new account below.");
        setError("That email isn't in our records.");
      } else if (status === 401 || status === 422 || /password|invalid|incorrect|credentials/i.test(detail || "")) {
        // Be helpful: 401 means EITHER wrong password OR no account at all. Surface both options inline.
        setPasswordError("Wrong password — or this email doesn't have an account yet.");
        setError("Wrong password — or this email doesn't have an account yet. Create a new account below.");
      } else {
        setError(typeof detail === "string" ? detail : "Sign-in failed. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout title={cfg.title === "Landlord sign-in" ? "Welcome back" : "Welcome back"} subtitle="Sign in to your Central Rentals account." accent={cfg.badgeText} accentClass={cfg.accentClass} PortalIcon={cfg.icon}>
      {/* Portal switcher — pill toggle */}
      <div className="flex p-1 rounded-full bg-slate-100 mb-6" data-testid="portal-switcher">
        {Object.entries(PORTALS).map(([k, p]) => (
          <button key={k} type="button" onClick={() => { setPortal(k); setError(""); setEmailError(""); setPasswordError(""); nav(`/login/${k}`, { replace: true }); }}
            data-testid={`portal-tab-${k}`}
            className={`flex-1 px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors ${portal === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"}`}>
            <p.icon className="w-4 h-4"/> {k === "landlord" ? "Landlord" : "Tenant"}
          </button>
        ))}
      </div>

      <form onSubmit={onSubmit} className="space-y-5" data-testid={`login-form-${portal}`}>
        <PillInput
          label="Email"
          type="email"
          value={email}
          onChange={(e) => { setEmail(e.target.value); setEmailError(""); }}
          placeholder="Enter your email"
          required
          autoComplete="email"
          testid="login-email-input"
          error={emailError}
        />
        <PillInput
          label="Password"
          type="password"
          value={password}
          onChange={(e) => { setPassword(e.target.value); setPasswordError(""); }}
          placeholder="Enter your password"
          required
          autoComplete="current-password"
          testid="login-password-input"
          error={passwordError}
          showToggle
        />
        <div className="flex justify-end -mt-1">
          <Link to="/forgot" data-testid="login-forgot-link" className="text-sm font-semibold text-slate-900 hover:underline underline-offset-4">
            Forgot password?
          </Link>
        </div>
        {error && (
          <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-2xl px-4 py-3 flex items-start gap-2" data-testid="login-error">
            <ShieldCheck className="w-4 h-4 mt-0.5 text-rose-500 flex-none"/>
            <span>{error}</span>
          </div>
        )}
        <MathCaptcha {...captcha} testId="login-captcha" />
        <button
          type="submit"
          disabled={submitting || !captcha.token || !captcha.answer.trim()}
          data-testid="login-submit-button"
          className="w-full inline-flex items-center justify-center gap-2 h-14 rounded-full bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white text-base font-bold shadow-sm transition-colors"
        >
          {submitting ? "Signing in…" : "Go to your account"}
        </button>
        <div className="flex items-center justify-center pt-1">
          <div className="text-xs text-slate-500 inline-flex items-center gap-1.5">
            <Lock className="w-3.5 h-3.5"/> Trusted login · TLS 1.3
          </div>
        </div>
      </form>

      {/* Divider */}
      <div className="mt-7 flex items-center gap-3 text-xs text-slate-400">
        <div className="flex-1 h-px bg-slate-200"/>
        <span>or</span>
        <div className="flex-1 h-px bg-slate-200"/>
      </div>

      {/* Signup redirect */}
      <div className="mt-6 text-center text-sm text-slate-600" data-testid="login-signup-strip">
        {cfg.signupHint}{" "}
        <Link to={cfg.signupTo} className="font-bold text-slate-900 hover:underline underline-offset-4 inline-flex items-center gap-1" data-testid="login-to-signup-link">
          Create an account <ArrowRight className="w-4 h-4"/>
        </Link>
      </div>
    </AuthLayout>
  );
}

function PillInput({ label, error, testid, required, showToggle, type = "text", ...props }) {
  const [visible, setVisible] = useState(false);
  const actualType = showToggle ? (visible ? "text" : "password") : type;
  return (
    <label className="block">
      {label && (
        <div className="text-sm font-bold text-slate-900 mb-2">
          {label}
          {required && <span className="text-rose-600 ml-1">*</span>}
        </div>
      )}
      <div className="relative">
        <input
          data-testid={testid}
          required={required}
          type={actualType}
          aria-required={required ? "true" : undefined}
          className={`w-full h-14 rounded-2xl bg-slate-50 border ${error ? "border-rose-400 focus:border-rose-500" : "border-slate-200 focus:border-slate-900"} text-slate-900 placeholder:text-slate-400 px-5 ${showToggle ? "pr-12" : ""} text-base focus:ring-2 focus:ring-slate-900/10 outline-none transition-colors`}
          {...props}
        />
        {showToggle && (
          <button
            type="button"
            onClick={() => setVisible((v) => !v)}
            data-testid={`${testid}-toggle`}
            aria-label={visible ? "Hide password" : "Show password"}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-1"
          >
            {visible ? <Eye className="w-5 h-5"/> : <ClosedEyeLashes className="w-5 h-5"/>}
          </button>
        )}
      </div>
      {error && <div className="text-xs text-rose-600 mt-2 flex items-center gap-1.5"><AlertCircle className="w-3.5 h-3.5"/>{error}</div>}
    </label>
  );
}

 
function _ContactStrip() {
  return (
    <div className="mt-8 pt-5 border-t border-slate-200 flex items-center justify-center text-xs text-slate-500" data-testid="auth-contact-strip">
      <a href={`mailto:${EMAIL}`} className="inline-flex items-center gap-1.5 hover:text-slate-900"><Mail className="w-3.5 h-3.5"/> {EMAIL}</a>
    </div>
  );
}

export function AuthLayout({ title, subtitle, accent, accentClass = "text-emerald-700", PortalIcon, children }) {
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Top nav — minimal, back-to-home only */}
      <div className="w-full px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-900" data-testid="auth-back-home">
          <ArrowLeft className="w-4 h-4"/> Back to home
        </Link>
        <Link to="/" data-testid="auth-logo-link" aria-label="Central Rentals Canada" className="select-none">
          <span className="font-display font-black tracking-tight text-slate-900 text-sm sm:text-base">
            Central Rentals Canada
          </span>
        </Link>
      </div>
      {/* Centered form card */}
      <div className="flex-1 flex items-start sm:items-center justify-center px-4 sm:px-6 pb-16">
        <div className="w-full max-w-md">
          <div className="rounded-3xl bg-white border border-slate-200 shadow-[0_4px_24px_rgba(15,23,42,0.06)] p-6 sm:p-8">
            {accent && (
              <div className={`inline-flex items-center gap-1.5 mb-4 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-100 text-[10px] font-bold tracking-[0.18em] uppercase ${accentClass}`}>
                {PortalIcon && <PortalIcon className="w-3 h-3"/>} {accent}
              </div>
            )}
            <h1 className="font-display text-2xl sm:text-3xl font-black tracking-tight text-slate-900 leading-tight">{title}</h1>
            {subtitle && <p className="text-slate-500 mt-2 mb-6 text-sm sm:text-base leading-relaxed">{subtitle}</p>}
            {children}
          </div>
          {/* Contact link outside the card */}
          <div className="mt-6 text-center text-xs text-slate-500 flex items-center justify-center gap-1.5">
            <Mail className="w-3.5 h-3.5"/>
            <a href={`mailto:${EMAIL}`} className="hover:text-slate-900">{EMAIL}</a>
          </div>
        </div>
      </div>
    </div>
  );
}
